import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { Button, CloseIcon, ConfirmDialog, IconButton, NumberInput } from "@a2z/ui";
import type {
  OrderStatus,
  SellerOrderDetail,
  SellerOrderListItem,
} from "@/lib/api/types";
import { getOrderItemImageUrl } from "@/lib/order-image";
import { useQuickStatusAction } from "../services/useQuickStatusAction";
import { getOrderStatusLabel } from "./order-status";
import { getAllowedTransitions } from "../lib/orderStatusMachine";
import { getSellerPath } from "@/lib/routing/portal-urls";
import { formatCurrency } from "@/lib/formatting/currency";

function mapQuickActionError(
  code: string | null,
  t: ReturnType<typeof useTranslations>,
): string | null {
  if (!code) return null;
  switch (code) {
    case "ETA_REQUIRED":
      return t(keys.orders.etaRequiredForStatusUpdate);
    case "ETA_INVALID":
    case "ETA_VALIDATION":
      return t(keys.orders.etaMustBeValidNumber);
    case "STALE_STATUS":
      return t(keys.orders.paymentReviewConflictNotice);
    default:
      return t(keys.errors.generic);
  }
}

type Props = {
  orderId: string | null;
  order: SellerOrderListItem | null;
  isOpen: boolean;
  storeSlug: string;
  locale: string;
  onClose: () => void;
  onSuccess: (updatedOrder: SellerOrderDetail) => void;
};

export function OrderQuickActionSheet({
  orderId,
  order,
  isOpen,
  storeSlug,
  locale,
  onClose,
  onSuccess,
}: Props) {
  const t = useTranslations();
  const touchStartY = useRef<number | null>(null);
  const [etaInput, setEtaInput] = useState("");
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);

  const { submitAction, isSaving, error, clearError } = useQuickStatusAction({
    storeSlug,
    orderId: orderId ?? "",
    onSuccess,
  });

  const contextualActions = useMemo(
    () => (order ? getAllowedTransitions(order.status) : []),
    [order],
  );

  const primaryAction =
    contextualActions.find((a) => a.variant === "primary") ?? null;
  const declineAction =
    contextualActions.find((a) => a.variant === "danger") ?? null;

  // Reset local state when the sheet opens (or switches orders while open) —
  // render-time previous-value comparison instead of a setState-in-effect.
  const resetKey = isOpen ? `open:${orderId ?? ""}` : "closed";
  const [prevResetKey, setPrevResetKey] = useState(resetKey);
  if (resetKey !== prevResetKey) {
    setPrevResetKey(resetKey);
    if (isOpen) {
      setEtaInput("");
      setShowDeclineConfirm(false);
      clearError();
    }
  }

  // Escape key handler
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isSaving) {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, isSaving, onClose]);

  if (!isOpen || !orderId || !order) {
    return null;
  }

  const errorMessage = mapQuickActionError(error, t);
  const openOrderHref = `${getSellerPath(locale, storeSlug, "/orders")}?order=${orderId}`;

  const handlePrimaryAction = () => {
    if (!primaryAction) return;

    if (primaryAction.needsEta) {
      const trimmed = etaInput.trim();
      if (!trimmed) {
        void submitAction(primaryAction.targetStatus);
        return;
      }
      void submitAction(primaryAction.targetStatus, Number(trimmed));
    } else {
      void submitAction(primaryAction.targetStatus);
    }
  };

  const handleDecline = () => {
    setShowDeclineConfirm(true);
  };

  const confirmDecline = () => {
    setShowDeclineConfirm(false);
    void submitAction("declined");
  };

  const requestClose = () => {
    if (isSaving) return;
    onClose();
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={requestClose}
        aria-hidden="true"
      />
      <div className="fixed inset-0 z-50 flex items-end md:items-center md:justify-center md:p-4">
        <div
          role="dialog"
          aria-modal="true"
          onTouchStart={(event) => {
            touchStartY.current = event.changedTouches[0]?.clientY ?? null;
          }}
          onTouchEnd={(event) => {
            const startY = touchStartY.current;
            const endY = event.changedTouches[0]?.clientY ?? null;
            if (startY !== null && endY !== null && endY - startY > 80) {
              requestClose();
            }
            touchStartY.current = null;
          }}
          className="flex w-full flex-col rounded-t-2xl border border-[color:var(--border-light)] bg-[color:var(--surface)] shadow-[var(--shadow-lg)] md:max-w-md md:rounded-2xl"
        >
          {/* Drag handle — mobile only */}
          <div
            className="flex justify-center pt-3 md:hidden"
            aria-hidden="true"
          >
            <div className="h-1 w-8 rounded-full bg-[color:var(--border-light)]" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between border-b border-[color:var(--border-light)] px-4 py-3 sm:px-5">
            <div className="min-w-0">
              <h3 className="truncate text-base font-semibold text-[color:var(--text-primary)]">
                {order.orderCode}
              </h3>
              <p className="truncate text-xs text-[color:var(--text-muted)]">
                {order.customer.name}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {/* Open order link — header icon */}
              <a
                href={openOrderHref}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-[color:var(--text-muted)] hover:bg-[color:var(--surface-alt)] hover:text-[color:var(--text-primary)]"
                title={t(keys.orders.openOrderAction)}
              >
                <ExternalLinkIcon />
              </a>
              <IconButton
                variant="outline"
                size="md"
                onClick={requestClose}
                ariaLabel={t(keys.common.close)}
                icon={<CloseIcon size={18} />}
              />
            </div>
          </div>

          {/* Body */}
          <div className="px-4 py-4 sm:px-5">
            {/* Order summary — amount, primary item, buyer phone — so the
                seller has enough context to decide without leaving the sheet. */}
            <div className="flex items-start gap-3 rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] p-3">
              {(() => {
                const firstItem = order.items[0];
                const thumbUrl = firstItem ? getOrderItemImageUrl(firstItem) : null;
                return thumbUrl ? (
                  <img
                    src={thumbUrl}
                    alt=""
                    className="h-12 w-12 shrink-0 rounded-lg border border-[color:var(--border-light)] object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="h-12 w-12 shrink-0 rounded-lg border border-[color:var(--border-light)] bg-[color:var(--surface)]" />
                );
              })()}
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-[color:var(--text-primary)]">
                  {locale === "en"
                    ? (order.items[0]?.resolvedTitleEn ??
                      order.items[0]?.productNameEn ??
                      order.items[0]?.resolvedTitleAr ??
                      order.items[0]?.productNameAr ??
                      "")
                    : (order.items[0]?.resolvedTitleAr ??
                      order.items[0]?.productNameAr ??
                      "")}
                </div>
                <div className="mt-0.5 text-xs text-[color:var(--text-muted)]">
                  {order.itemCount > 1
                    ? `+${order.itemCount - 1}`
                    : null}{" "}
                  {order.customer.phoneE164}
                </div>
              </div>
              <div className="shrink-0 text-base font-bold text-[color:var(--text-primary)]">
                {formatCurrency(order.totalAmount, locale)}
              </div>
            </div>

            {/* Status transition visual */}
            <div className="mt-3">
              <StatusTransitionVisual
                t={t}
                currentStatus={order.status}
                nextStatus={primaryAction?.targetStatus ?? null}
              />
            </div>

            {/* Error display */}
            {errorMessage ? (
              <div className="mt-3 rounded-lg border border-[color:var(--error-500)]/35 bg-[color:var(--error-500)]/10 px-3 py-2 text-sm text-[color:var(--error-500)]">
                {errorMessage}
              </div>
            ) : null}

            {/* Inline ETA input when primary action needs it */}
            {primaryAction?.needsEta ? (
              <div className="mt-3">
                <label className="flex flex-col gap-1 text-xs text-[color:var(--text-muted)]">
                  {t(keys.orders.estimatedDeliveryLabel)}
                  <NumberInput
                    name="quickEta"
                    min={0}
                    value={etaInput}
                    onChange={(event) => setEtaInput(event.target.value)}
                    className="h-11 rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 text-sm"
                  />
                  <span className="text-[13px] text-[color:var(--text-muted)]">
                    {t(keys.orders.estimatedDeliveryHelper)}
                  </span>
                </label>
              </div>
            ) : null}
          </div>

          {/* Footer actions */}
          <div className="flex flex-wrap items-center gap-2 border-t border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:px-5">
            {primaryAction ? (
              <Button
                type="button"
                variant="primary"
                className="h-11 flex-1 rounded-lg px-4 text-sm font-semibold"
                onClick={handlePrimaryAction}
                disabled={isSaving}
              >
                {isSaving
                  ? t(keys.orders.savingAction)
                  : t(primaryAction.labelKey)}
              </Button>
            ) : null}
            {declineAction ? (
              <Button
                type="button"
                variant="outline"
                className="h-11 rounded-lg border-[color:var(--error-500)]/40 px-4 text-sm font-semibold text-[color:var(--error-500)] hover:bg-[color:var(--error-500)]/5"
                onClick={handleDecline}
                disabled={isSaving}
              >
                {t(declineAction.labelKey)}
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeclineConfirm}
        title={t(keys.seller.orderDeclineConfirmTitle)}
        body={t(keys.seller.orderDeclineConfirmMessage)}
        confirmLabel={t(keys.seller.orderActionDecline)}
        cancelLabel={t(keys.common.cancel)}
        isSubmitting={isSaving}
        onConfirm={confirmDecline}
        onCancel={() => setShowDeclineConfirm(false)}
      />
    </>
  );
}

// --- Sub-components ---

function StatusTransitionVisual({
  t,
  currentStatus,
  nextStatus,
}: {
  t: ReturnType<typeof useTranslations>;
  currentStatus: OrderStatus;
  nextStatus: OrderStatus | null;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="inline-flex items-center rounded-full border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] px-2.5 py-1 text-xs font-medium text-[color:var(--text-secondary)]">
        {getOrderStatusLabel(t, currentStatus)}
      </span>
      {nextStatus ? (
        <>
          <ArrowEndIcon />
          <span className="inline-flex items-center rounded-full border border-[color:var(--primary-200)] bg-[color:var(--primary-50)] px-2.5 py-1 text-xs font-semibold text-[color:var(--primary-600)]">
            {getOrderStatusLabel(t, nextStatus)}
          </span>
        </>
      ) : null}
    </div>
  );
}

function ArrowEndIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0 text-[color:var(--text-disabled)] rtl:rotate-180"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

function ExternalLinkIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}
